import { useState, useMemo, useEffect } from "react";
import { useSnackbar } from "../../../../contexts/snackbar-context";
import { Component,ComponentType } from "../../../../types/component";
import { useAirframeService } from "../../../../services/airframeService";
import { useComponentService } from "../../../../services/componentService";
import { Airframe, InstalledComponent } from "../../../../types/airframe";
import { OffWingType } from "../../../../types/DetachComponent";
import { ShopVisitReasonComponentType } from "../../../../types/shopVisitReason";
import {
  airframeSupportsLandingGearSubAssemblies,
  ComponentSubAssemblyAttachInfo,
  componentHasAttachedSubAssemblies,
  isLandingGearComponent,
  loadLandingGearSubAssemblyAttachInfo,
} from "../../../../utils/landingGearAttachUtils";

export type { ComponentSubAssemblyAttachInfo };

type ConfirmDetachState = {
  open: boolean;
  step: "confirm" | "offWing";
  componentId: string;
  airframeId?: string;
  slotId?: string;
  offWingType: OffWingType;
  shopVisitReasonId?: string | null;
  shopVisitReasonLabel?: string | null;
  componentType?: ShopVisitReasonComponentType | null;
  skipOffWingStep: boolean;
};

// Helper to extract component type from the slot
const extractComponentType = (airframe: Airframe, component: Component): string | null => {
  const slotId = component?.slotId;
  const componentType =
    slotId
      ? airframe.slots?.find(
          slot => slot?.id === slotId && slot?.installedComponent !== null
        )?.componentType ?? null
      : null;

  return componentType;
};

const ComponentTypeMap: Record<number, ComponentType> = {
  0: "Engine",
  1: "APU",
  2: "Landing Gear",
  3: "Airframe",
};

const normalizeId = (value: string | undefined): string =>
  value ? value.trim().toLowerCase() : "";

const normalizeComponentTypeForReason = (value: unknown): ShopVisitReasonComponentType | null => {
  const raw = String(value ?? "").trim().toLowerCase();
  if (!raw) return null;

  if (raw === "engine" || raw === "0") return "Engine";
  if (raw === "apu" || raw === "1") return "APU";
  if (raw === "landinggear" || raw === "landing gear" || raw === "2") return "LandingGear";
  return null;
};

export function useComponentAttach(
  components: Component[],
  airframes: Airframe[] = [],
  onReload?: () => void
) {
  const [openAttach, setOpenAttach] = useState(false);
  const [openReplace, setOpenReplace] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState<Airframe | null>(null);
  const [replaceTarget, setReplaceTarget] = useState<Component | null>(null);
  const [form, setForm] = useState({ componentId: "", slotId: "" });
  const [confirmDetach, setConfirmDetach] = useState<ConfirmDetachState>({
    open: false,
    step: "confirm",
    componentId: "",
    offWingType: "In Shop",
    skipOffWingStep: false,
  });
  const [isAttaching, setIsAttaching] = useState(false);
  const [isReplacing, setIsReplacing] = useState(false);
  const [isDetaching, setIsDetaching] = useState(false);

  const { showError, showSuccess } = useSnackbar();
  const { attachComponentToAirframe, detachComponentFromAirframe, replaceComponentInAirframe } = useAirframeService();
  const { getComponentSubAssemblies } = useComponentService();
  const [componentType, setComponentType] = useState<ComponentType | null>(null);
  const [subAssemblyByComponentId, setSubAssemblyByComponentId] = useState<
    Record<string, ComponentSubAssemblyAttachInfo>
  >({});
  const [subAssemblyInfoLoading, setSubAssemblyInfoLoading] = useState(false);

  const templateSupportsSubAssemblies = useMemo(
    () => airframeSupportsLandingGearSubAssemblies(selectedPkg),
    [selectedPkg]
  );

  /** Lookup component by ID */
  const getComponentById = (componentId: string): Component | undefined => {
    return components.find(c => c.id === componentId);
  };

  const getComponentDisplayName = (componentId: string): string => {
    const component = getComponentById(componentId);
    if (!component) return "Unknown Assembly";
    return `${component.model} - ${component.serialNumber}`;
  };

  //Get all installed component IDs across all airframes 
  const installedComponentIds = useMemo(() => {
    const ids = new Set<string>();
    // Collect all component IDs that are installed in any airframe slot
    airframes.forEach(airframe => {
      if (airframe.slots) {
        airframe.slots.forEach(slot => {
          const componentId = normalizeId(slot.installedComponent?.componentId);
          if (componentId) {
            ids.add(componentId);
          }
        });
      }
    });
    return ids;
  }, [airframes]);

  /** Components NOT attached to any package */
  const free = useMemo(() => {
    const freeComps = components.filter(c => {
      const componentId = normalizeId(c.id);
      return (
        componentId &&
        c.isDeleted === false &&
        (!c.attachedToAirframe || c.attachedToAirframe === "") &&
        !installedComponentIds.has(componentId)
      );
    });
    return freeComps;
  }, [components, installedComponentIds]);

  useEffect(() => {
    if (!openAttach && !openReplace) {
      setSubAssemblyByComponentId({});
      setSubAssemblyInfoLoading(false);
      return;
    }

    const componentById = new Map<string, Component>();
    for (const component of free) {
      if (component.id) {
        componentById.set(normalizeId(component.id), component);
      }
    }
    if (
      openReplace &&
      replaceTarget?.id &&
      isLandingGearComponent(replaceTarget)
    ) {
      componentById.set(normalizeId(replaceTarget.id), replaceTarget);
    }

    const landingGearIds = Array.from(componentById.keys()).filter((id) => {
      const component = componentById.get(id);
      return component && isLandingGearComponent(component);
    });

    if (landingGearIds.length === 0) {
      setSubAssemblyByComponentId({});
      setSubAssemblyInfoLoading(false);
      return;
    }

    let cancelled = false;
    setSubAssemblyInfoLoading(true);

    void (async () => {
      const entries = await Promise.all(
        landingGearIds.map(async (normalizedId) => {
          const component = componentById.get(normalizedId);
          if (!component?.id) return null;

          const attachInfo = await loadLandingGearSubAssemblyAttachInfo(
            component.id,
            component,
            getComponentSubAssemblies
          );

          if (!attachInfo) return null;
          return [component.id, attachInfo] as const;
        })
      );

      if (cancelled) return;

      const next: Record<string, ComponentSubAssemblyAttachInfo> = {};
      for (const entry of entries) {
        if (entry) {
          next[normalizeId(entry[0])] = entry[1];
        }
      }
      setSubAssemblyByComponentId(next);
      setSubAssemblyInfoLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [openAttach, openReplace, free, replaceTarget, getComponentSubAssemblies]);

  const attachableComponents = useMemo(() => {
    const hasFreeLandingGear = free.some(isLandingGearComponent);

    if (hasFreeLandingGear && subAssemblyInfoLoading) {
      return free.filter((component) => !isLandingGearComponent(component));
    }

    return free.filter((component) => {
      if (!isLandingGearComponent(component)) return true;

      const hasSubAssemblies = componentHasAttachedSubAssemblies(
        component,
        subAssemblyByComponentId[normalizeId(component.id)]
      );

      return templateSupportsSubAssemblies
        ? hasSubAssemblies
        : !hasSubAssemblies;
    });
  }, [
    free,
    templateSupportsSubAssemblies,
    subAssemblyByComponentId,
    subAssemblyInfoLoading,
  ]);

  const componentsToReplace = useMemo(() => {
    const availableReplacingComp = components.filter((c) => {
      const componentId = normalizeId(c.id);
      const typeStr = ComponentTypeMap[Number(c.componentType)];
      return (
        c.id &&
        c.isDeleted === false &&
        (!c.attachedToAirframe || c.attachedToAirframe === "") &&
        !installedComponentIds.has(componentId) &&
        (!componentType || typeStr === componentType)
      );
    });

    if (!openReplace || !replaceTarget || !isLandingGearComponent(replaceTarget)) {
      return availableReplacingComp;
    }

    const hasFreeLandingGear = availableReplacingComp.some(isLandingGearComponent);
    if (hasFreeLandingGear && subAssemblyInfoLoading) {
      return availableReplacingComp.filter((component) => !isLandingGearComponent(component));
    }

    const targetHasSubAssemblies = componentHasAttachedSubAssemblies(
      replaceTarget,
      subAssemblyByComponentId[normalizeId(replaceTarget.id)]
    );
    const targetPosition = replaceTarget.attachmentLocation?.trim().toUpperCase() ?? "";

    return availableReplacingComp.filter((component) => {
      if (!isLandingGearComponent(component)) return true;

      const candidateHasSubAssemblies = componentHasAttachedSubAssemblies(
        component,
        subAssemblyByComponentId[normalizeId(component.id)]
      );

      if (targetHasSubAssemblies !== candidateHasSubAssemblies) {
        return false;
      }

      if (targetHasSubAssemblies && targetPosition) {
        const candidatePosition =
          subAssemblyByComponentId[normalizeId(component.id)]?.landingGearType
            ?.trim()
            .toUpperCase() ?? "";
        if (candidatePosition && candidatePosition !== targetPosition) {
          return false;
        }
      }

      return true;
    });
  }, [
    components,
    installedComponentIds,
    componentType,
    openReplace,
    replaceTarget,
    subAssemblyByComponentId,
    subAssemblyInfoLoading,
  ]);

  useEffect(() => {
    if (!openAttach || !form.componentId) return;

    if (!attachableComponents.some((component) => component.id === form.componentId)) {
      setForm({ componentId: "", slotId: "" });
    }
  }, [openAttach, form.componentId, attachableComponents]);

  useEffect(() => {
    if (!openReplace || !form.componentId) return;

    if (!componentsToReplace.some((component) => component.id === form.componentId)) {
      setForm((prev) => ({ ...prev, componentId: "" }));
    }
  }, [openReplace, form.componentId, componentsToReplace]);

  /** Components attached to a specific package (by tail number) */
  const attached = (packageIdentifier: string) => {
    const attachedComps = components.filter(
      (c) => c.attachedToAirframe && c.attachedToAirframe === packageIdentifier
    );
    return attachedComps;
  };

  const resolveSlotComponentId = (installed: InstalledComponent | null | undefined) => {
    if (!installed) {
      return "";
    }
    const raw = installed as { componentId?: string; ComponentId?: string };
    return normalizeId(raw.componentId ?? raw.ComponentId);
  };

  // Get installed components for a specific airframe from slots
  const getInstalledComponentsForAirframe = (airframe: Airframe) => {
    const installed: Component[] = [];
    if (!airframe.slots) return installed;

    airframe.slots.forEach((slot) => {
      const componentId = resolveSlotComponentId(slot.installedComponent);
      if (!componentId) {
        return;
      }

      const component = components.find((c) => normalizeId(c.id) === componentId);
      if (component) {
        installed.push({
          ...component,
          attachedToAirframe: airframe.tailNumber,
          attachmentLocation: slot.locationCode,
          airframeId: airframe.id,
          slotId: slot.id,
          slotNumber: slot.slotNumber,
        });
      }
    });

    return installed;
  };

  const openAttachDialog = (pkg: Airframe) => {
    setSelectedPkg(pkg);
    setForm({ componentId: "", slotId: "" });
    setOpenAttach(true);
  };

  const openReplaceDialog = (pkg: Airframe, c: Component) => {
    if (!pkg.id || !c.id) return;

    setSelectedPkg(pkg);
    setReplaceTarget(c);
    setComponentType(extractComponentType(pkg,c));

    setForm({
      componentId: "",
      slotId: "",
    });

    setOpenReplace(true);
  };

  const attach = async () => {
    if (!form.componentId || !form.slotId || !selectedPkg?.id) {
      showError("Select assembly and slot");
      return;
    }

    setIsAttaching(true);
    try {
      await attachComponentToAirframe(
        selectedPkg.id,
        form.componentId,
        form.slotId
      );

      showSuccess("Assembly attached");
      setOpenAttach(false);
      setForm({ componentId: "", slotId: "" });
      
      // Wait for reload to complete before closing dialog
      if (onReload) {
        await onReload();
      }
    } catch (error) {
      showError("Attach failed");
    } finally {
      setIsAttaching(false);
    }
  };

  const replace = async () => {
    if (!replaceTarget?.id || !selectedPkg?.id || !form.componentId) {
      showError("Select replacement assembly");
      return;
    }

    setIsReplacing(true);
    try {
      await replaceComponentInAirframe(
        selectedPkg.id,
        replaceTarget.id,
        form.componentId
      );

      showSuccess("Assembly replaced");
      setOpenReplace(false);
      setForm({ componentId: "", slotId: "" });
      if (onReload) {
        await onReload();
      }
    } catch {
      showError("Replace failed");
    } finally {
      setIsReplacing(false);
    }
  };

  const detach = async (componentId: string, airframeId?: string, slotId?: string) => {
    if (!componentId || !slotId || !airframeId) {
      showError("Select assembly to detach");
      return;
    }

    const normalizedComponentId = normalizeId(componentId);
    const component = components.find((c) => normalizeId(c.id) === normalizedComponentId);
    const airframe = airframes.find((a) => normalizeId(a.id) === normalizeId(airframeId));
    const slot = airframe?.slots?.find((s) => normalizeId(s.id) === normalizeId(slotId));

    const componentType =
      normalizeComponentTypeForReason(component?.type) ??
      normalizeComponentTypeForReason(component?.componentType) ??
      normalizeComponentTypeForReason(slot?.componentType);

    let skipOffWingStep = false;
    if (component && isLandingGearComponent(component)) {
      try {
        const subAssemblies = await getComponentSubAssemblies(componentId);
        skipOffWingStep = subAssemblies.length > 0;
      } catch {
        skipOffWingStep = false;
      }
    }

    setConfirmDetach({
      open: true,
      step: "confirm",
      componentId,
      airframeId,
      slotId,
      offWingType: "In Shop",
      shopVisitReasonId: null,
      shopVisitReasonLabel: null,
      componentType,
      skipOffWingStep,
    });
  };

  const handleConfirmDetach = async () => {
    if (confirmDetach.skipOffWingStep) {
      await handleSaveDetach();
      return;
    }

    setConfirmDetach(prev => ({
      ...prev,
      step: "offWing",
      offWingType: prev.offWingType || "In Shop",
    }));
  };

  const handleSaveDetach = async () => {
    const { componentId, airframeId, slotId, offWingType, skipOffWingStep } = confirmDetach;
    if (!componentId || !slotId || !airframeId) {
      showError("Select assembly to detach");
      return;
    }

    if (!skipOffWingStep) {
      if (!offWingType) {
        showError("Select off-wing type");
        return;
      }

      if (offWingType === "In Shop" && !confirmDetach.shopVisitReasonId) {
        showError("Select in-shop reason");
        return;
      }
    }

    setIsDetaching(true);
    try {
      await detachComponentFromAirframe(
        airframeId,
        componentId,
        slotId,
        skipOffWingStep ? undefined : offWingType,
        confirmDetach.shopVisitReasonId,
        confirmDetach.shopVisitReasonLabel
      );

      showSuccess("Assembly detached");
      setConfirmDetach({
        open: false,
        step: "confirm",
        componentId: "",
        offWingType: "In Shop",
        shopVisitReasonId: null,
        shopVisitReasonLabel: null,
        componentType: null,
        skipOffWingStep: false,
      });
      if (onReload) {
        await onReload();
      }
    } catch (error) {
      showError("Detach failed");
      setConfirmDetach({
        open: false,
        step: "confirm",
        componentId: "",
        offWingType: "In Shop",
        shopVisitReasonId: null,
        shopVisitReasonLabel: null,
        componentType: null,
        skipOffWingStep: false,
      });
    } finally {
      setIsDetaching(false);
    }
  };

  return {
    free,
    attachableComponents,
    templateSupportsSubAssemblies,
    subAssemblyByComponentId,
    attached,
    getInstalledComponentsForAirframe,
    getComponentById,
    getComponentDisplayName,
    form,
    setForm,
    openAttach,
    openReplace,
    selectedPkg,
    replaceTarget,
    openAttachDialog,
    openReplaceDialog,
    attach,
    replace,
    detach,
    setOpenAttach,
    setOpenReplace,
    confirmDetach,
    setConfirmDetach,
    handleConfirmDetach,
    handleSaveDetach,
    componentsToReplace,
    subAssemblyInfoLoading,
    isAttaching,
    isReplacing,
    isDetaching
  };
}
